import assign from "lodash/fp/assign";
import apis from "../../core/apis.js";

class CorporateResource {
  constructor($resource, Upload) {
    "ngInject";
    this.Upload = Upload;
    this.$resource = $resource;
  }

  search(query = "", params) {
    return this.$resource(apis.corporate + "autocomplete/").get(
      assign(params, { query })
    );
  }

  getAll(options) {
    return this.$resource(apis.corporate).get(options);
  }

  get(id) {
    return this.$resource(apis.corporate + id).get();
  }

  getInvoiceItem(id) {
    return this.$resource(apis.corporateInvoiceItems + id).get();
  }

  post(item) {
    return this.$resource(apis.corporate).save(item);
  }

  update(id, item) {
    return this.$resource(apis.corporate + id, null, {
      update: { method: "PATCH" },
    }).update(item);
  }

  getAllContracts(options) {
    return this.$resource(
      apis.corporate + options.id + "/corporate_contracts",
      null,
      { get: { isArray: true } }
    ).get(options);
  }

  getAllUsers(options) {
    return this.$resource(apis.corporate + options.id + "/users").get(options);
  }

  getAllAdmins(options) {
    return this.$resource(
      apis.corporate + options.id + "/users?corporate_role=admin"
    ).get(options);
  }

  getAllHolidays(options) {
    return this.$resource(apis.corporate + "holidays").get(options);
  }

  getAllOfficeLocations(options) {
    return this.$resource(
      apis.corporate + options.id + "/office_locations"
    ).get({
      page: options.page,
      limit: options.limit,
      is_active: options.is_active,
    });
  }

  getAllShifts(options) {
    const { id, ...params } = options;
    return this.$resource(apis.corporate + id + "/shifts").get({
      ...params,
      page: options.page,
      limit: options.limit,
    });
  }

  exportAllShiftsReservations(options) {
    const { id, ...params } = options;
    return this.$resource(
      apis.corporate + id + "/shifts/export_reservations",
      null,
      {
        get: {
          method: "GET",
          responseType: "text",
          transformResponse: (res) => ({ data: angular.fromJson(res) }),
        },
      }
    ).get(params);
  }

  getShiftsOfficeTimes(options) {
    const { id, ...params } = options;
    return this.$resource(apis.corporate + id + "/shifts/times").get({
      ...params,
    });
  }

  createHoliday(holiday) {
    return this.$resource(
      apis.corporate + "holidays?corporate=" + holiday.corporate
    ).save({
      name: holiday.name,
      date: holiday.date,
    });
  }

  createOfficeLocation({ location, name, corporate }) {
    return this.$resource(apis.corporate + corporate + "/office_location").save(
      {
        name,
        coords: {
          lat: location.coordinates[1],
          lng: location.coordinates[0],
        },
      }
    );
  }

  createShift({ corporate: corporateId, ...shiftData }) {
    return this.$resource(apis.corporate + corporateId + "/shift").save({
      start_date: shiftData.start_date,
      end_date: shiftData.end_date,
      direction: shiftData.direction,
      time: shiftData.time,
      weekdays: shiftData.weekdays,
      office_location: shiftData.office_location,
    });
  }

  getHolidayDetails(id) {
    const url = `${apis.corporate}holidays/${id}`;
    return this.$resource(url).get();
  }

  updateHoliday(id, item) {
    const url = `${apis.corporate}holidays/${id}`;
    return this.$resource(url, null, {
      update: { method: "PATCH" },
    }).update(item);
  }

  getAllInvoices(options) {
    return this.$resource(apis.corporateInvoices).get(options);
  }

  getInvoiceInfo(id) {
    return this.$resource(apis.corporateInvoices + id).get();
  }

  uploadSettlement(id, data) {
    const url = apis.corporateInvoices + id + "/add_settlement";
    const request = this.Upload.upload({ url, data });
    return { $promise: request };
  }

  uploadBulkUsers(id, data) {
    const url = `${apis.corporate}${id}/upload/users`;
    const request = this.Upload.upload({ url, data });
    return { $promise: request };
  }

  getLastBulkUpload(options) {
    return this.$resource(
      apis.corporate + options.corporate_id + "/upload/" + options.upload_id
    ).get(options.type);
  }

  getCorporateInvoiceItems(options) {
    return this.$resource(apis.corporateInvoiceItems).get(options);
  }

  deactivate(id, data) {
    return this.$resource(
      apis.corporateInvoiceItems + id + "/deactivate",
      null,
      { update: { method: "PATCH" } }
    ).update(data);
  }

  activate(id, data) {
    return this.$resource(apis.corporateInvoiceItems + id + "/activate", null, {
      update: { method: "PATCH" },
    }).update(data);
  }

  addNote(id, item) {
    return this.$resource(apis.corporateInvoiceItems + id, null, {
      update: { method: "PATCH" },
    }).update(item);
  }

  createCorporateAdmin(id, data) {
    return this.$resource(
      apis.corporate + "add_corporate_admin?corporate=" + id
    ).save({
      ...data,
    });
  }

  resetAdminPassword(id, data) {
    const { _id: corporateAdmin } = data;
    return this.$resource(
      apis.corporate + "reset_password?corporate_admin=" + corporateAdmin
    ).save({});
  }

  updateAdminLookerRoles(data) {
    const { _id: corporateAdminId, ...newData } = data;
    return this.$resource(
      apis.corporate + "corporate_admin/" + corporateAdminId + "/looker_roles",
      null,
      {
        update: { method: "PUT" },
      }
    ).update({
      ...newData,
    });
  }

  removeAdmin(id, data) {
    const { _id: corporateAdminId } = data;
    return this.$resource(
      apis.corporate + "corporate_admin/" + corporateAdminId
    ).delete({});
  }

  removeOfficeLocation(corporateId, data) {
    const { _id: officeLocationId } = data;
    return this.$resource(
      apis.corporate + corporateId + "/office_location/" + officeLocationId
    ).delete({});
  }

  removeShift(corporateId, data) {
    const { _id: shiftId } = data;
    return this.$resource(
      apis.corporate + corporateId + "/shift/" + shiftId
    ).delete({});
  }

  setDefaultOfficeLocation(corporateId, data) {
    const { _id: officeLocationId } = data;
    return this.$resource(
      apis.corporate +
        corporateId +
        "/office_location/" +
        officeLocationId +
        "/set_default",
      null,
      { update: { method: "PUT" } }
    ).update({});
  }

  changeOfficeLocationStatus(corporateId, data) {
    const { _id: officeLocationId, is_active: isActive } = data;
    return this.$resource(
      apis.corporate + corporateId + "/office_location/" + officeLocationId,
      null,
      { update: { method: "PUT" } }
    ).update({
      is_active: isActive,
    });
  }

  updateOfficeLocation(corporateId, data) {
    const { officeLocationId, name, coords } = data;
    return this.$resource(
      apis.corporate + corporateId + "/office_locations/" + officeLocationId,
      null,
      { update: { method: "PATCH" } }
    ).update({
      name,
      coords,
    });
  }

  changeShiftStatus(corporateId, data) {
    const { _id: shiftId, is_active: isActive } = data;
    return this.$resource(
      apis.corporate + corporateId + "/shift/" + shiftId + "/active",
      null,
      { update: { method: "PUT" } }
    ).update({
      is_active: isActive,
    });
  }
}

export default CorporateResource;



// WEBPACK FOOTER //
// ./src/resources/corporate/corporate.resource.js